import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PiggyBank,
  TrendingUp,
  Calendar,
  Wallet,
  AlertCircle,
  Eye,
  Pencil,
  Plus,
  Loader2,
  RefreshCw,
  Settings2,
  XCircle,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { PageHeader } from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { applicationsApi, cdiApi } from "@/services/api";

// Cores para os gráficos
const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

// Tipos

interface Application {
  id: string;
  applicationType: string;
  productName: string | null;
  principalCents: number;
  currentValueCents: number | null;
  totalRedeemedCents: number;
  purchaseDate: string;
  maturityDate: string | null;
  rateType: string | null;
  rateValue: string | null;
  rateDescription: string | null;
  status: string;
  totalEarningsCents: number;
  dailyEarningsCents: number | null;
  projectedMonthlyEarningsCents: number | null;
  liquidityDays: number | null;
  notes: string | null;
}

interface Template {
  id: string;
  descriptionPattern: string;
  patternType: string;
  applicationType: string;
  productName: string | null;
  rateType: string;
  rateValue: string;
  liquidityDays: number;
  hasMaturity: boolean;
  defaultMaturityDays: number | null;
  priority: number;
  isActive: boolean;
  notes: string | null;
}

// Funções auxiliares
const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString("pt-BR");
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return <Badge variant="success">Ativa</Badge>;
    case "NEEDS_RATE_CONFIG":
      return <Badge variant="warning">Aguardando Taxa</Badge>;
    case "MATURED":
      return <Badge variant="destructive">Vencida</Badge>;
    case "PARTIALLY_REDEEMED":
      return <Badge variant="secondary">Parcial</Badge>;
    case "FULLY_REDEEMED":
      return <Badge variant="outline">Resgatada</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
};

const getTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    CDB: "CDB",
    LCI: "LCI",
    LCA: "LCA",
    RDB: "RDB",
    POUPANCA: "Poupança",
    FUNDO: "Fundo",
    COMPROMISSADA: "Compromissada",
    OTHER: "Outros",
  };
  return labels[type] || type;
};

const getRateTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    PRE: "Prefixado",
    CDI: "CDI",
    IPCA: "IPCA",
    SELIC: "Selic",
    TR: "TR",
    OTHER: "Outros",
  };
  return labels[type] || type;
};

// Função para calcular dias úteis entre duas datas (aproximação)
const calculateBusinessDays = (startDate: Date, endDate: Date): number => {
  let count = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return Math.max(0, count - 1); // Não conta o dia da compra
};

// Função para calcular rendimento em tempo real
const calculateRealtimeYield = (
  app: Application,
  dailyCdiRate: number
): { currentValueCents: number; yieldCents: number } => {
  if (!app.rateType || !app.rateValue || !dailyCdiRate) {
    return {
      currentValueCents: app.currentValueCents || app.principalCents,
      yieldCents: app.totalEarningsCents,
    };
  }

  const purchaseDate = new Date(app.purchaseDate);
  const now = new Date();
  const businessDays = calculateBusinessDays(purchaseDate, now);

  if (businessDays <= 0) {
    return {
      currentValueCents: app.currentValueCents || app.principalCents,
      yieldCents: 0,
    };
  }

  // IMPORTANTE: Usar currentValueCents (saldo atual após resgates) como base
  // Se não existir, usar principalCents como fallback
  const baseValue = app.currentValueCents || app.principalCents;
  let accumulatedFactor = 1;

  if (app.rateType === "CDI") {
    // Taxa CDI - rateValue é o percentual do CDI (ex: 98.46 = 98.46% CDI)
    const rateMultiplier = parseFloat(app.rateValue) / 100;
    // dailyCdiRate já é a taxa diária em decimal (ex: 0.0005513)
    const dailyRate = dailyCdiRate * rateMultiplier;
    // Fator acumulado = (1 + taxa_diária)^dias_úteis
    accumulatedFactor = Math.pow(1 + dailyRate, businessDays);
  } else if (app.rateType === "PRE") {
    // Taxa prefixada - rateValue é a taxa anual
    const annualRate = parseFloat(app.rateValue) / 100;
    const dailyRate = Math.pow(1 + annualRate, 1 / 252) - 1;
    accumulatedFactor = Math.pow(1 + dailyRate, businessDays);
  } else if (app.rateType === "SELIC") {
    // SELIC ~ CDI
    const rateMultiplier = parseFloat(app.rateValue) / 100;
    const dailyRate = dailyCdiRate * rateMultiplier;
    accumulatedFactor = Math.pow(1 + dailyRate, businessDays);
  }

  // Calcular rendimento baseado no valor atual (após resgates)
  // O rendimento deve ser proporcional ao saldo atual, não ao principal original
  const currentValueCents = Math.round(baseValue * accumulatedFactor);
  // Rendimento é a diferença entre valor atual com rendimento e valor base
  const yieldCents = currentValueCents - baseValue;

  return { currentValueCents, yieldCents };
};

// Formatar rendimento com 4 casas decimais
const formatYield = (cents: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(cents / 100);
};

// Componente principal
export function ApplicationsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Modais
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  // Form states
  const [configForm, setConfigForm] = useState({
    rateType: "",
    rateValue: "",
    productName: "",
    liquidityDays: 0,
    createTemplate: false,
    descriptionPattern: "",
  });

  const [templateForm, setTemplateForm] = useState({
    descriptionPattern: "",
    patternType: "LIKE",
    applicationType: "CDB",
    productName: "",
    rateType: "CDI",
    rateValue: "",
    liquidityDays: 0,
    hasMaturity: false,
    defaultMaturityDays: 0,
    priority: 0,
    notes: "",
  });

  // Estado para seleção de template no modal de configuração
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  // Queries
  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ["applications-dashboard"],
    queryFn: () => applicationsApi.getDashboard(),
  });

  const { data: applications, isLoading: applicationsLoading } = useQuery({
    queryKey: ["applications", statusFilter, typeFilter],
    queryFn: () => applicationsApi.list({
      status: statusFilter !== "all" ? statusFilter : undefined,
      applicationType: typeFilter !== "all" ? typeFilter : undefined,
    }),
  });

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ["application-templates"],
    queryFn: () => applicationsApi.listTemplates(),
  });

  useQuery({
    queryKey: ["applications-needs-config"],
    queryFn: () => applicationsApi.getNeedsConfig(),
  });

  // Buscar taxa CDI atual para cálculo em tempo real
  const { data: currentCdi } = useQuery({
    queryKey: ["cdi-current"],
    queryFn: () => cdiApi.getCurrent(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Buscar rendimento calculado em tempo real (mesma lógica do Dashboard)
  const { data: yieldSummary } = useQuery({
    queryKey: ["applications-yield-summary"],
    queryFn: () => applicationsApi.getYieldSummary(),
    staleTime: 60 * 1000, // 1 minuto
  });

  // Taxa CDI diária em decimal (ex: 0.0005513 para 0.05513%)
  const dailyCdiRate = currentCdi?.rate ? parseFloat(currentCdi.rate) / 100 : 0;

  // Mutations
  const updateApplicationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      applicationsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["applications-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["applications-needs-config"] });
      setShowConfigModal(false);
      setSelectedApplication(null);
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: (data: any) => applicationsApi.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-templates"] });
      setShowTemplateModal(false);
      setEditingTemplate(null);
      resetTemplateForm();
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      applicationsApi.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-templates"] });
      setShowTemplateModal(false);
      setEditingTemplate(null);
      resetTemplateForm();
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => applicationsApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-templates"] });
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: ({ applicationId, templateId }: { applicationId: string; templateId: string }) =>
      applicationsApi.applyTemplate(applicationId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["applications-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["applications-needs-config"] });
      setShowConfigModal(false);
      setSelectedApplication(null);
    },
  });

  const calculateEarningsMutation = useMutation({
    mutationFn: () => applicationsApi.calculateEarnings(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["applications-dashboard"] });
    },
  });

  const resetTemplateForm = () => {
    setTemplateForm({
      descriptionPattern: "",
      patternType: "LIKE",
      applicationType: "CDB",
      productName: "",
      rateType: "CDI",
      rateValue: "",
      liquidityDays: 0,
      hasMaturity: false,
      defaultMaturityDays: 0,
      priority: 0,
      notes: "",
    });
  };

  const handleConfigureRate = (app: Application) => {
    setSelectedApplication(app);
    setSelectedTemplateId("");
    setConfigForm({
      rateType: app.rateType || "CDI",
      rateValue: app.rateValue || "",
      productName: app.productName || "",
      liquidityDays: app.liquidityDays || 0,
      createTemplate: false,
      descriptionPattern: "",
    });
    setShowConfigModal(true);
  };

  const handleViewApplication = (app: Application) => {
    setSelectedApplication(app);
    setShowViewModal(true);
  };

  const handleSaveConfig = () => {
    if (!selectedApplication) return;

    updateApplicationMutation.mutate({
      id: selectedApplication.id,
      data: {
        rateType: configForm.rateType,
        rateValue: configForm.rateValue,
        productName: configForm.productName,
        liquidityDays: configForm.liquidityDays,
        status: "ACTIVE",
      },
    });

    // Se marcou para criar template, criar também
    if (configForm.createTemplate && configForm.descriptionPattern) {
      createTemplateMutation.mutate({
        descriptionPattern: configForm.descriptionPattern,
        applicationType: selectedApplication.applicationType,
        productName: configForm.productName,
        rateType: configForm.rateType,
        rateValue: configForm.rateValue,
        liquidityDays: configForm.liquidityDays,
      });
    }
  };

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setTemplateForm({
      descriptionPattern: template.descriptionPattern,
      patternType: template.patternType,
      applicationType: template.applicationType,
      productName: template.productName || "",
      rateType: template.rateType,
      rateValue: template.rateValue,
      liquidityDays: template.liquidityDays,
      hasMaturity: template.hasMaturity,
      defaultMaturityDays: template.defaultMaturityDays || 0,
      priority: template.priority,
      notes: template.notes || "",
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = () => {
    if (editingTemplate) {
      updateTemplateMutation.mutate({
        id: editingTemplate.id,
        data: templateForm,
      });
    } else {
      createTemplateMutation.mutate(templateForm);
    }
  };

  // Preparar dados para gráficos
  const earningsChartData = dashboard?.recentEarnings?.map((e) => ({
    date: e.date.slice(5), // MM-DD
    rendimento: e.earningsCents / 100,
  })) || [];

  const typeChartData = dashboard?.byType?.map((t, i) => ({
    name: getTypeLabel(t.type),
    value: t.volumeCents / 100,
    color: CHART_COLORS[i % CHART_COLORS.length],
  })) || [];

  const fifoChartData = dashboard?.fifoSchedule?.map((f) => {
    // f.month é YYYY-MM, converter para "Mês/AA"
    const [year, month] = f.month.split("-");
    const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const monthName = monthNames[parseInt(month, 10) - 1] || month;
    return {
      month: `${monthName}/${year.slice(2)}`,
      valor: f.maturingCents / 100,
      tipo: f.byType,
    };
  }) || [];

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<PiggyBank className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Aplicações Financeiras"
        description="Gerencie suas aplicações, rendimentos e templates de taxa"
        stats={[
          {
            label: "Volume Total",
            value: formatCurrency(dashboard?.totalVolumeCents || 0),
          },
          {
            label: "Rendimento Total",
            value: formatCurrency(yieldSummary?.totalYieldCents || 0),
          },
          {
            label: "Aguardando Config",
            value: String(dashboard?.needsConfigCount || 0),
          },
        ]}
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => calculateEarningsMutation.mutate()}
            disabled={calculateEarningsMutation.isPending}
          >
            {calculateEarningsMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Calcular Rendimentos
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="applications">
            Aplicações
            {(dashboard?.needsConfigCount || 0) > 0 && (
              <Badge variant="warning" className="ml-2">
                {dashboard?.needsConfigCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        {/* TAB DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboardLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
            </div>
          ) : (
            <>
              {/* Cards de Métricas */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-8 h-8 opacity-80" />
                      <div>
                        <p className="text-sm opacity-80">Volume Aplicado</p>
                        <p className="text-2xl font-bold">
                          {formatCurrency(dashboard?.totalVolumeCents || 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-0">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 opacity-80" />
                      <div>
                        <p className="text-sm opacity-80">Valor Atual</p>
                        <p className="text-2xl font-bold">
                          {formatCurrency(dashboard?.totalCurrentValueCents || 0)}
                        </p>
                        <p className="text-xs opacity-70">
                          +{formatCurrency(dashboard?.totalEarningsCents || 0)} acumulado
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-600 to-amber-700 text-white border-0">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-8 h-8 opacity-80" />
                      <div>
                        <p className="text-sm opacity-80">Rendimento Total</p>
                        <p className="text-2xl font-bold">
                          {formatCurrency(yieldSummary?.totalYieldCents || 0)}
                        </p>
                        <p className="text-xs opacity-70">
                          Projeção: {formatCurrency(dashboard?.projectedMonthlyEarningsCents || 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white border-0">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <PiggyBank className="w-8 h-8 opacity-80" />
                      <div>
                        <p className="text-sm opacity-80">Aplicações</p>
                        <p className="text-2xl font-bold">{dashboard?.applicationCount || 0}</p>
                        <p className="text-xs opacity-70">
                          {dashboard?.activeCount || 0} ativas
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Alertas */}
              {(dashboard?.needsConfigCount || 0) > 0 && (
                <Card className="border-amber-500/50 bg-amber-500/5">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="font-medium text-amber-500">
                          {dashboard?.needsConfigCount} aplicação(ões) aguardando configuração de taxa
                        </p>
                        <p className="text-sm text-neutral-400">
                          Configure a taxa para que os rendimentos sejam calculados automaticamente
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        onClick={() => setActiveTab("applications")}
                      >
                        Configurar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Gráficos */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Rendimentos Recentes */}
                <Card>
                  <CardHeader>
                    <CardTitle>Rendimentos dos Últimos 30 Dias</CardTitle>
                    <CardDescription>Evolução diária dos rendimentos</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {earningsChartData.length === 0 ? (
                      <div className="h-[300px] flex flex-col items-center justify-center text-neutral-500">
                        <TrendingUp className="w-12 h-12 mb-4 opacity-50" />
                        <p>Nenhum rendimento registrado</p>
                        <p className="text-sm mt-1">
                          Configure as taxas das aplicações para iniciar o cálculo
                        </p>
                      </div>
                    ) : (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={earningsChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey="date" stroke="#666" fontSize={12} />
                            <YAxis stroke="#666" fontSize={12} />
                            <Tooltip
                              contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
                              formatter={(value) => [formatCurrency((value as number) * 100), "Rendimento"]}
                            />
                            <Line
                              type="monotone"
                              dataKey="rendimento"
                              stroke="#10b981"
                              strokeWidth={2}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Distribuição por Tipo */}
                <Card>
                  <CardHeader>
                    <CardTitle>Distribuição por Tipo</CardTitle>
                    <CardDescription>Volume aplicado por tipo de aplicação</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={typeChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                          >
                            {typeChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
                            formatter={(value) => [formatCurrency((value as number) * 100), "Volume"]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* FIFO de Maturidade */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Cronograma FIFO de Maturidade</CardTitle>
                    <CardDescription>Valores disponíveis para resgate sem IOF (30 dias após aplicação)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {fifoChartData.length === 0 ? (
                      <div className="h-[300px] flex flex-col items-center justify-center text-neutral-500">
                        <Calendar className="w-12 h-12 mb-4 opacity-50" />
                        <p>Nenhuma aplicação registrada</p>
                        <p className="text-sm mt-1">
                          As aplicações aparecerão aqui após serem importadas
                        </p>
                      </div>
                    ) : (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={fifoChartData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                            <XAxis
                              dataKey="month"
                              stroke="#666"
                              fontSize={12}
                              tickLine={false}
                              axisLine={false}
                            />
                            <YAxis
                              stroke="#666"
                              fontSize={12}
                              tickLine={false}
                              axisLine={false}
                              tickFormatter={(value) => {
                                if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
                                if (value >= 1000) return `R$ ${(value / 1000).toFixed(0)}K`;
                                return `R$ ${value}`;
                              }}
                            />
                            <Tooltip
                              contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333", borderRadius: "8px" }}
                              labelStyle={{ color: "#fff", fontWeight: "bold", marginBottom: "4px" }}
                              formatter={(value) => [formatCurrency((value as number) * 100), "Disponível para resgate"]}
                            />
                            <Bar dataKey="valor" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* TAB APLICAÇÕES */}
        <TabsContent value="applications" className="space-y-4">
          {/* Filtros */}
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="ACTIVE">Ativas</SelectItem>
                <SelectItem value="NEEDS_RATE_CONFIG">Aguardando Taxa</SelectItem>
                <SelectItem value="MATURED">Vencidas</SelectItem>
                <SelectItem value="FULLY_REDEEMED">Resgatadas</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                <SelectItem value="CDB">CDB</SelectItem>
                <SelectItem value="LCI">LCI</SelectItem>
                <SelectItem value="LCA">LCA</SelectItem>
                <SelectItem value="FUNDO">Fundo</SelectItem>
                <SelectItem value="POUPANCA">Poupança</SelectItem>
                <SelectItem value="COMPROMISSADA">Compromissada</SelectItem>
                <SelectItem value="OTHER">Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tabela */}
          <Card>
            <CardContent className="p-0">
              {applicationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
              ) : !applications?.length ? (
                <div className="text-center py-12 text-neutral-500">
                  Nenhuma aplicação encontrada
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border bg-muted/30">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-4 font-medium">Data</th>
                        <th className="p-4 font-medium">Tipo</th>
                        <th className="p-4 font-medium">Produto</th>
                        <th className="p-4 font-medium text-right">Principal</th>
                        <th className="p-4 font-medium text-right">Atual</th>
                        <th className="p-4 font-medium text-right">Rendimento</th>
                        <th className="p-4 font-medium text-center">Taxa</th>
                        <th className="p-4 font-medium text-center">Status</th>
                        <th className="p-4 font-medium text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app: Application) => {
                        // Calcular rendimento em tempo real
                        const { currentValueCents, yieldCents } = calculateRealtimeYield(app, dailyCdiRate);

                        return (
                        <tr
                          key={app.id}
                          className="border-b border-border last:border-0 hover:bg-muted/20"
                        >
                          <td className="p-4">
                            <p className="font-medium">{formatDate(app.purchaseDate)}</p>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline">{getTypeLabel(app.applicationType)}</Badge>
                          </td>
                          <td className="p-4">
                            <p className="text-muted-foreground">{app.productName || "-"}</p>
                          </td>
                          <td className="p-4 text-right">
                            {formatCurrency(app.principalCents)}
                          </td>
                          <td className="p-4 text-right">
                            {formatCurrency(currentValueCents)}
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-green-500">
                              +{formatYield(yieldCents)}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            {app.rateType ? (
                              <span>{app.rateValue}% {getRateTypeLabel(app.rateType)}</span>
                            ) : (
                              <span className="text-amber-500">Não configurada</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {getStatusBadge(app.status)}
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {(app.status === "NEEDS_RATE_CONFIG" || !app.rateType) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleConfigureRate(app)}
                                  title="Configurar taxa"
                                >
                                  <Settings2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewApplication(app)}
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB TEMPLATES */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setEditingTemplate(null);
                resetTemplateForm();
                setShowTemplateModal(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {templatesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : !templates?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Settings2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum template configurado</p>
                  <p className="text-sm mt-1">
                    Templates permitem configurar automaticamente novas aplicações
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border bg-muted/30">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-4 font-medium">Padrão de Descrição</th>
                        <th className="p-4 font-medium">Tipo</th>
                        <th className="p-4 font-medium">Produto</th>
                        <th className="p-4 font-medium text-center">Taxa</th>
                        <th className="p-4 font-medium text-center">Liquidez</th>
                        <th className="p-4 font-medium text-center">Prioridade</th>
                        <th className="p-4 font-medium text-center">Status</th>
                        <th className="p-4 font-medium text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((template: Template) => (
                        <tr
                          key={template.id}
                          className="border-b border-border last:border-0 hover:bg-muted/20"
                        >
                          <td className="p-4">
                            <p className="font-medium font-mono text-sm">{template.descriptionPattern}</p>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline">{getTypeLabel(template.applicationType)}</Badge>
                          </td>
                          <td className="p-4">
                            <p className="text-muted-foreground">{template.productName || "-"}</p>
                          </td>
                          <td className="p-4 text-center">
                            {template.rateValue}% {getRateTypeLabel(template.rateType)}
                          </td>
                          <td className="p-4 text-center">
                            D+{template.liquidityDays}
                          </td>
                          <td className="p-4 text-center">
                            {template.priority}
                          </td>
                          <td className="p-4 text-center">
                            {template.isActive ? (
                              <Badge variant="success">Ativo</Badge>
                            ) : (
                              <Badge variant="secondary">Inativo</Badge>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditTemplate(template)}
                                title="Editar template"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-400"
                                onClick={() => deleteTemplateMutation.mutate(template.id)}
                                title="Excluir template"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Configuração de Taxa */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-xl mx-4">
            <CardHeader>
              <CardTitle>Configurar Taxa da Aplicação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-2">
                Defina a taxa de rentabilidade para esta aplicação
              </p>

              {/* Opção de usar template existente */}
              {templates && templates.length > 0 && (
                <div className="border rounded-lg p-4 bg-muted/20">
                  <label className="text-sm font-medium">Usar Template Existente</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Selecione um template para aplicar automaticamente as configurações
                  </p>
                  <div className="flex gap-2">
                    <Select
                      value={selectedTemplateId}
                      onValueChange={setSelectedTemplateId}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecione um template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template: Template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.productName || template.descriptionPattern} - {template.rateValue}% {getRateTypeLabel(template.rateType)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => {
                        if (selectedApplication && selectedTemplateId) {
                          applyTemplateMutation.mutate({
                            applicationId: selectedApplication.id,
                            templateId: selectedTemplateId,
                          });
                        }
                      }}
                      disabled={!selectedTemplateId || applyTemplateMutation.isPending}
                    >
                      {applyTemplateMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Aplicar"
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-muted-foreground">ou configure manualmente</span>
                <div className="flex-1 border-t border-border" />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Tipo de Taxa</label>
                  <Select
                    value={configForm.rateType}
                    onValueChange={(v) => setConfigForm({ ...configForm, rateType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CDI">CDI</SelectItem>
                      <SelectItem value="PRE">Prefixado</SelectItem>
                      <SelectItem value="SELIC">Selic</SelectItem>
                      <SelectItem value="IPCA">IPCA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Taxa (%) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={configForm.rateValue}
                    onChange={(e) => setConfigForm({ ...configForm, rateValue: e.target.value })}
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Liquidez (dias)</label>
                  <Input
                    type="number"
                    value={configForm.liquidityDays}
                    onChange={(e) => setConfigForm({ ...configForm, liquidityDays: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Nome do Produto</label>
                  <Input
                    value={configForm.productName}
                    onChange={(e) => setConfigForm({ ...configForm, productName: e.target.value })}
                    placeholder="Ex: CDB Selic"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configForm.createTemplate}
                    onChange={(e) => setConfigForm({ ...configForm, createTemplate: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium">Criar template para aplicações futuras</span>
                    <p className="text-xs text-muted-foreground">
                      Aplica esta configuração automaticamente em novas aplicações similares
                    </p>
                  </div>
                </label>

                {configForm.createTemplate && (
                  <div className="mt-4">
                    <label className="text-sm font-medium">Padrão de Descrição</label>
                    <Input
                      value={configForm.descriptionPattern}
                      onChange={(e) => setConfigForm({ ...configForm, descriptionPattern: e.target.value })}
                      placeholder="Ex: APLICACAO IDSELIC%"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use % como coringa para corresponder a qualquer texto
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowConfigModal(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveConfig}
                  disabled={updateApplicationMutation.isPending || !configForm.rateValue}
                >
                  {updateApplicationMutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Visualização */}
      {showViewModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-xl mx-4">
            <CardHeader>
              <CardTitle>Detalhes da Aplicação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-2">
                {getTypeLabel(selectedApplication.applicationType)} - {formatDate(selectedApplication.purchaseDate)}
              </p>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Tipo</label>
                  <p className="font-medium">{getTypeLabel(selectedApplication.applicationType)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedApplication.status)}</div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Principal</label>
                  <p className="font-medium">{formatCurrency(selectedApplication.principalCents)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Valor Atual</label>
                  <p className="font-medium">{formatCurrency(
                    selectedApplication.currentValueCents || selectedApplication.principalCents
                  )}</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Rendimento</label>
                  <p className="font-medium text-green-500">+{formatCurrency(selectedApplication.totalEarningsCents)}</p>
                </div>
                {(selectedApplication.totalRedeemedCents || 0) > 0 && (
                  <div>
                    <label className="text-sm text-muted-foreground">Resgatado</label>
                    <p className="font-medium text-amber-500">-{formatCurrency(selectedApplication.totalRedeemedCents)}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-muted-foreground">Taxa</label>
                  <p className="font-medium">
                    {selectedApplication.rateType
                      ? `${selectedApplication.rateValue}% ${getRateTypeLabel(selectedApplication.rateType)}`
                      : <span className="text-amber-500">Não configurada</span>
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Liquidez</label>
                  <p className="font-medium">
                    {selectedApplication.liquidityDays !== undefined && selectedApplication.liquidityDays !== null
                      ? `D+${selectedApplication.liquidityDays}`
                      : "-"
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Vencimento</label>
                  <p className="font-medium">
                    {selectedApplication.maturityDate ? formatDate(selectedApplication.maturityDate) : "-"}
                  </p>
                </div>
              </div>

              {selectedApplication.productName && (
                <div>
                  <label className="text-sm text-muted-foreground">Produto</label>
                  <p className="font-medium">{selectedApplication.productName}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                {selectedApplication.status === "NEEDS_RATE_CONFIG" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewModal(false);
                      handleConfigureRate(selectedApplication);
                    }}
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    Configurar Taxa
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowViewModal(false)}>
                  Fechar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Template */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle>{editingTemplate ? "Editar Template" : "Novo Template"}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-2">
                Configure um padrão para identificar e configurar aplicações automaticamente
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Padrão de Descrição *</label>
                  <Input
                    value={templateForm.descriptionPattern}
                    onChange={(e) => setTemplateForm({ ...templateForm, descriptionPattern: e.target.value })}
                    placeholder="Ex: APLICACAO IDSELIC%"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use % como coringa para corresponder a qualquer texto
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Nome do Produto</label>
                  <Input
                    value={templateForm.productName}
                    onChange={(e) => setTemplateForm({ ...templateForm, productName: e.target.value })}
                    placeholder="Ex: CDB Selic Empresa Itaú"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Tipo de Aplicação</label>
                  <Select
                    value={templateForm.applicationType}
                    onValueChange={(v) => setTemplateForm({ ...templateForm, applicationType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CDB">CDB</SelectItem>
                      <SelectItem value="LCI">LCI</SelectItem>
                      <SelectItem value="LCA">LCA</SelectItem>
                      <SelectItem value="FUNDO">Fundo</SelectItem>
                      <SelectItem value="POUPANCA">Poupança</SelectItem>
                      <SelectItem value="COMPROMISSADA">Compromissada</SelectItem>
                      <SelectItem value="OTHER">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Tipo de Taxa</label>
                  <Select
                    value={templateForm.rateType}
                    onValueChange={(v) => setTemplateForm({ ...templateForm, rateType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CDI">CDI</SelectItem>
                      <SelectItem value="PRE">Prefixado</SelectItem>
                      <SelectItem value="SELIC">Selic</SelectItem>
                      <SelectItem value="IPCA">IPCA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Taxa (%) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={templateForm.rateValue}
                    onChange={(e) => setTemplateForm({ ...templateForm, rateValue: e.target.value })}
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Liquidez (dias)</label>
                  <Input
                    type="number"
                    value={templateForm.liquidityDays}
                    onChange={(e) => setTemplateForm({ ...templateForm, liquidityDays: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Prioridade</label>
                  <Input
                    type="number"
                    value={templateForm.priority}
                    onChange={(e) => setTemplateForm({ ...templateForm, priority: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Maior prioridade = verificado primeiro
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Observações</label>
                  <Input
                    value={templateForm.notes}
                    onChange={(e) => setTemplateForm({ ...templateForm, notes: e.target.value })}
                    placeholder="Anotações sobre este template..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveTemplate}
                  disabled={
                    createTemplateMutation.isPending ||
                    updateTemplateMutation.isPending ||
                    !templateForm.descriptionPattern ||
                    !templateForm.rateValue
                  }
                >
                  {(createTemplateMutation.isPending || updateTemplateMutation.isPending) && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
